import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can, colourHexFor } from '@pl/domain';
import type { SkuDto } from '@pl/domain';
import { type FormEvent, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { qk, useMe, useSkus } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, SkuTag, Spinner, Swatch } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import s from './SettingsSkus.module.css';

const HEX = /^#[0-9a-fA-F]{6}$/;

/** What the modal is editing: a SKU, or `null` for a new one. */
type Editing = { sku: SkuDto | null; key: number };

function matches(sku: SkuDto, q: string): boolean {
  if (!q) return true;
  const hay = [sku.code, sku.name, sku.colourName ?? '', ...Object.entries(sku.attributes).map(([k, v]) => `${k} ${v}`)].join(' ').toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => hay.includes(word));
}

export function SettingsSkus() {
  const meQuery = useMe();
  const skusQuery = useSkus({ includeInactive: true });
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);

  const skus = useMemo(() => skusQuery.data ?? [], [skusQuery.data]);
  const visible = useMemo(() => skus.filter((sku) => matches(sku, search.trim())), [skus, search]);
  const activeCount = skus.filter((x) => x.active).length;

  const me = meQuery.data;
  if (!me) return <Spinner label="Loading SKUs" />;
  const canEdit = can(me.role, 'settings');
  const open = (sku: SkuDto | null) => setEditing({ sku, key: Date.now() });

  return (
    <div className="panel">
      <div className="panel-h">
        <h2>SKUs</h2>
        {skusQuery.data && (
          <Chip>
            {activeCount} active{skus.length > activeCount ? ` · ${skus.length - activeCount} inactive` : ''}
          </Chip>
        )}
        <span className="grow" />
        {!canEdit && <span className="sub">Only an admin can add or change SKUs</span>}
        <div className={`search ${s.skSearch}`}>
          <Icon name="search" />
          <input
            type="search"
            className={s.skSearchInput}
            aria-label="Search SKUs"
            placeholder="Code, name, color or attribute"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canEdit && (
          <button type="button" className="btn primary sm" onClick={() => open(null)}>
            <Icon name="plus" />
            Add SKU
          </button>
        )}
      </div>

      <RefetchNote queries={[skusQuery]} className={s.skNote} />
      {skusQuery.isLoadingError ? (
        <div className={s.skNote}>
          <ErrorNote error={skusQuery.error} />
          <button type="button" className="btn" onClick={() => skusQuery.refetch()}>
            Try again
          </button>
        </div>
      ) : skusQuery.isPending ? (
        <div className="panel-b">
          <Spinner label="Loading SKUs" />
        </div>
      ) : skus.length === 0 ? (
        <Empty
          title="No SKUs yet"
          description={canEdit ? 'Add them one at a time, or import the spreadsheet you keep them in now.' : 'An admin adds SKUs here, or imports them from a spreadsheet.'}
          action={
            can(me.role, 'import') ? (
              <Link className="link arrow" to="/office/settings/import">
                Import a spreadsheet
              </Link>
            ) : undefined
          }
        />
      ) : visible.length === 0 ? (
        <Empty title={`No SKUs match “${search.trim()}”`} />
      ) : (
        <table className={`t ${s.skTable}`}>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Attributes</th>
              <th>Active</th>
              {canEdit && (
                <th className="r">
                  <span className={s.skHidden}>Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map((sku) => {
              const attrs = Object.entries(sku.attributes);
              return (
                <tr key={sku.id}>
                  <td className={s.skSkuCell}>
                    <SkuTag sku={sku} withCode />
                  </td>
                  <td>
                    {attrs.length === 0 ? (
                      <span className="faint">—</span>
                    ) : (
                      <span className={s.skAttrs}>
                        {attrs.map(([k, v]) => (
                          <Chip key={k}>
                            {k}: {String(v)}
                          </Chip>
                        ))}
                      </span>
                    )}
                  </td>
                  <td>
                    {sku.active ? (
                      <Chip tone="ok" dot>
                        Active
                      </Chip>
                    ) : (
                      <Chip>Inactive</Chip>
                    )}
                  </td>
                  {canEdit && (
                    <td className="r">
                      <button type="button" className="btn sm" aria-label={`Edit ${sku.code}`} onClick={() => open(sku)}>
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {canEdit && editing && <SkuModal key={editing.key} sku={editing.sku} onClose={() => setEditing(null)} />}
    </div>
  );
}

/**
 * Add or edit one SKU. The hex follows the color name while nobody has typed a hex of their own —
 * "Blue" fills in the house blue — and stops following the moment someone does.
 */
function SkuModal({ sku, onClose }: { sku: SkuDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const id = useId();
  const [code, setCode] = useState(sku?.code ?? '');
  const [name, setName] = useState(sku?.name ?? '');
  const [colour, setColour] = useState(sku?.colourName ?? '');
  const [hex, setHex] = useState(sku?.colourHex ?? '');
  // An existing SKU whose hex is not the one its color name gives was set by hand: keep it.
  const [hexOwn, setHexOwn] = useState(() => Boolean(sku?.colourHex && sku.colourHex.toLowerCase() !== (colourHexFor(sku.colourName) ?? '').toLowerCase()));
  const [active, setActive] = useState(sku?.active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const base = { code: code.trim(), name: name.trim(), colourName: colour.trim() || null };
      const colourHex = hex.trim() ? hex.trim().toLowerCase() : null;
      // A blank hex is left to the API, which derives it from the color name when it knows it.
      return sku ? api.patch<SkuDto>(`/skus/${sku.id}`, { ...base, colourHex, active }) : api.post<SkuDto>('/skus', colourHex ? { ...base, colourHex } : base);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.allSkus });
      onClose();
    },
  });

  const hexValid = hex.trim() === '' || HEX.test(hex.trim());
  const canSave = Boolean(code.trim() && name.trim() && hexValid && !save.isPending);
  const known = colourHexFor(colour);
  const title = sku ? 'Edit SKU' : 'Add SKU';

  function edited() {
    // A 409 names the code as it was typed; once the code changes it is about something else.
    if (save.isError) save.reset();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canSave) save.mutate();
  }

  const preview: SkuDto = {
    id: sku?.id ?? 'preview',
    code: code.trim() || 'CODE',
    name: name.trim() || 'Name',
    colourName: colour.trim() || null,
    colourHex: HEX.test(hex.trim()) ? hex.trim() : null,
    attributes: {},
    tracking: 'box',
    active,
  };

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
      footer={
        <>
          <span className="sub">{sku ? 'Shown this way everywhere' : 'The code must be unique'}</span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={`${id}-form`} className="btn primary" disabled={!canSave}>
            <Icon name="check" />
            Save SKU
          </button>
        </>
      }
    >
      <form id={`${id}-form`} className={s.skForm} onSubmit={submit} noValidate>
        {save.isError && <ErrorNote error={save.error} />}
        <div className={s.skTwo}>
          <div className="fld">
            <label htmlFor={`${id}-code`}>Code</label>
            <input
              id={`${id}-code`}
              // Radix would otherwise focus the first tabbable thing, the Close button; the office
              // types straight into the code.
              autoFocus
              className={`inp ${s.skMono}`}
              value={code}
              maxLength={64}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setCode(e.target.value);
                edited();
              }}
            />
          </div>
          <div className="fld">
            <label htmlFor={`${id}-name`}>Name</label>
            <input
              id={`${id}-name`}
              className="inp"
              value={name}
              maxLength={200}
              autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                edited();
              }}
            />
          </div>
        </div>
        <div className={s.skTwo}>
          <div className="fld">
            <label htmlFor={`${id}-colour`}>Color</label>
            <input
              id={`${id}-colour`}
              className="inp"
              value={colour}
              maxLength={60}
              autoComplete="off"
              placeholder="Blue"
              onChange={(e) => {
                const next = e.target.value;
                setColour(next);
                if (!hexOwn) setHex(colourHexFor(next) ?? '');
                edited();
              }}
            />
          </div>
          <div className="fld">
            <label htmlFor={`${id}-hex`}>Hex</label>
            <div className={s.skHexRow}>
              <Swatch hex={HEX.test(hex.trim()) ? hex.trim() : null} name={colour || null} size="lg" />
              <input
                id={`${id}-hex`}
                className={`inp ${s.skMono} ${hexValid ? '' : 'err'}`}
                value={hex}
                maxLength={7}
                autoComplete="off"
                spellCheck={false}
                placeholder="#1e5bd8"
                aria-invalid={!hexValid}
                aria-describedby={`${id}-hex-help`}
                onChange={(e) => {
                  setHex(e.target.value);
                  setHexOwn(e.target.value.trim() !== '');
                  edited();
                }}
              />
            </div>
            <span id={`${id}-hex-help`} className={hexValid ? s.skHelp : s.skHelpBad}>
              {!hexValid
                ? 'Six hex digits after a #, like #1e5bd8.'
                : !hexOwn && known
                  ? 'Filled in from the color name.'
                  : 'Optional. Filled in for common colors.'}
            </span>
          </div>
        </div>
        {sku && (
          <label className={s.skCheck}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>
              Active
              <small>Inactive SKUs stay on every box and order that has them, but can’t be picked for new stickers or orders.</small>
            </span>
          </label>
        )}
        <div className={s.skPreview} aria-hidden="true">
          <span className="sec-k">Shows as</span>
          <SkuTag sku={preview} withCode />
        </div>
      </form>
    </Modal>
  );
}
